import axios from "axios";
import { API_BASE_URL } from "../apiConstants";

// Create API client instance with base configuration
const dialerApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor — emit loading event; authentication uses httpOnly cookies
dialerApi.interceptors.request.use(
  (config) => {
    window.dispatchEvent(new CustomEvent("apiRequestStart"));
    return config;
  },
  (error) => {
    window.dispatchEvent(new CustomEvent("apiRequestEnd"));
    return Promise.reject(error);
  },
);

// Response interceptor for loading state management
dialerApi.interceptors.response.use(
  (response) => {
    // Dispatch loading complete event
    window.dispatchEvent(new CustomEvent("apiRequestEnd"));
    return response;
  },
  (error) => {
    // Dispatch loading complete event on error
    window.dispatchEvent(new CustomEvent("apiRequestEnd"));
    return Promise.reject(error);
  },
);

// Type validation helper
const validateTypes = (params, validations) => {
  Object.entries(validations).forEach(([key, type]) => {
    if (params[key] !== undefined && typeof params[key] !== type) {
      throw new Error(
        `Invalid type for ${key}: expected ${type}, got ${typeof params[key]}`,
      );
    }
  });
};

// API methods with error handling and type validation
export default {
  /**
   * Barge into an active call
   * @param {Object} params
   * @param {string} params.callId - ID of the call to barge into
   * @param {string} params.agentId - ID of the agent barging in
   */
  bargeIntoCall: async (params) => {
    try {
      validateTypes(params, {
        callId: "string",
        agentId: "string",
      });

      const response = await dialerApi.post("/barge", params);
      return response.data;
    } catch (error) {
      // Normalize network errors consistently across dialer requests.
      if (!error.response) {
        throw new Error("Network error - Please check your connection");
      }

      switch (error.response.status) {
        case 400:
          throw new Error("Invalid request parameters");
        case 401:
          throw new Error("Authentication required");
        case 500:
          throw new Error("Server error - Please try again later");
        default:
          throw new Error(`API Error: ${error.response.statusText}`);
      }
    }
  },

  /**
   * Get current call status
   * @param {Object} params
   * @param {string} params.callId - ID of the call to check
   */
  getCallStatus: async (params) => {
    try {
      validateTypes(params, {
        callId: "string",
      });

      const response = await dialerApi.get(`/calls/${params.callId}`);
      return response.data;
    } catch (error) {
      // Normalize network errors consistently across dialer requests.
      if (!error.response) {
        throw new Error("Network error - Please check your connection");
      }

      switch (error.response.status) {
        case 400:
          throw new Error("Invalid request parameters");
        case 401:
          throw new Error("Authentication required");
        case 500:
          throw new Error("Server error - Please try again later");
        default:
          throw new Error(`API Error: ${error.response.statusText}`);
      }
    }
  },

  /**
   * Barge into an active call
   * @param {Object} params
   * @param {string} params.callId - ID of the call to barge into
   * @param {string} params.agentId - ID of the agent barging in
   * @returns {Promise<Object>} API response data
   */
  bargeIn: async (params) => {
    try {
      // Validate required parameters
      validateTypes(params, {
        callId: "string",
        agentId: "string",
      });

      const response = await dialerApi.post("/barge", {
        callId: params.callId,
        agentId: params.agentId,
      });

      return response.data;
    } catch (error) {
      // Reuse existing error handling logic
      if (!error.response) {
        throw new Error("Network error - Please check your connection");
      }

      switch (error.response.status) {
        case 400:
          throw new Error("Invalid request parameters");
        case 401:
          throw new Error("Authentication required");
        case 500:
          throw new Error("Server error - Please try again later");
        default:
          throw new Error(`API Error: ${error.response.statusText}`);
      }
    }
  },

  /**
   * Set pause state for an agent in a queue
   * @param {Object} params
   * @param {string} params.queueName - Name of the queue
   * @param {boolean} params.paused - Pause state (true to pause, false to unpause)
   */
  /**
   * Toggle mute state for an active call
   * @param {Object} params
   * @param {string} params.callId - ID of the call to mute/unmute
   * @param {boolean} params.muted - true to mute, false to unmute
   */
  toggleMute: async (params) => {
    try {
      validateTypes(params, { callId: "string", muted: "boolean" });
      const endpoint = params.muted
        ? `/calls/${params.callId}/mute`
        : `/calls/${params.callId}/unmute`;
      const response = await dialerApi.put(endpoint);
      return response.data;
    } catch (error) {
      if (!error.response)
        throw new Error("Network error - Please check your connection");
      switch (error.response.status) {
        case 401:
          throw new Error("Authentication required");
        case 404:
          throw new Error("Call not found");
        case 500:
          throw new Error("Server error - Please try again later");
        default:
          throw new Error(`API Error: ${error.response.statusText}`);
      }
    }
  },

  /**
   * Send a DTMF digit to an active call via the backend.
   * Wires to PUT /calls/:id/dtmf with body { digit }.
   * @param {string} callId - ID of the active call
   * @param {string} digit - Single DTMF digit (0-9, *, #)
   * @returns {Promise<Object>} API response data
   */
  sendDTMF: async (callId, digit) => {
    try {
      if (typeof callId !== 'string' || !callId) {
        throw new Error('callId is required');
      }
      if (typeof digit !== 'string' || !digit) {
        throw new Error('digit is required');
      }
      const response = await dialerApi.put(`/calls/${callId}/dtmf`, { digit });
      return response.data;
    } catch (error) {
      if (!error.response) throw new Error('Network error - Please check your connection');
      switch (error.response.status) {
        case 401: throw new Error('Authentication required');
        case 404: throw new Error('Call not found');
        case 422: throw new Error('Digit is required');
        case 502: throw new Error('Telephony backend error sending DTMF');
        case 500: throw new Error('Server error - Please try again later');
        default: throw new Error(`API Error: ${error.response.statusText}`);
      }
    }
  },

  /**
   * Blind-transfer an active call to a new extension.
   * Wires to PUT /calls/:id/transfer with body { extension, context }.
   * @param {string} callId - ID of the active call
   * @param {string} extension - Destination extension/number
   * @param {string} [context="from-internal"] - Dialplan context
   * @returns {Promise<Object>} API response data
   */
  transferCall: async (callId, extension, context = 'from-internal') => {
    try {
      if (typeof callId !== 'string' || !callId) throw new Error('callId is required');
      if (typeof extension !== 'string' || !extension) throw new Error('extension is required');
      const response = await dialerApi.put(`/calls/${callId}/transfer`, { extension, context });
      return response.data;
    } catch (error) {
      if (!error.response) throw new Error('Network error - Please check your connection');
      switch (error.response.status) {
        case 401: throw new Error('Authentication required');
        case 404: throw new Error('Call not found');
        case 422: throw new Error('Extension is required');
        case 502: throw new Error('Telephony backend error transferring call');
        case 500: throw new Error('Server error - Please try again later');
        default: throw new Error(`API Error: ${error.response.statusText}`);
      }
    }
  },

  /**
   * Place an active call on hold.
   * @param {string} callId - ID of the active call
   */
  /**
   * Warm-transfer an active call to a same-organization rep. The server
   * resolves the rep's extension, transfers the call and sends the handoff
   * note to that rep's agent channel.
   * @param {string} callId - ID of the active call
   * @param {{targetUserId: string, handoffNote?: string}} target
   * @returns {Promise<Object>} API response data
   */
  transferToAgent: async (callId, { targetUserId, handoffNote = '' }) => {
    if (typeof callId !== 'string' || !callId) throw new Error('callId is required');
    if (typeof targetUserId !== 'string' || !targetUserId) throw new Error('Choose who to transfer to');
    try {
      const response = await dialerApi.put(`/calls/${callId}/transfer`, {
        target_user_id: targetUserId,
        handoff_note: handoffNote,
      });
      return response.data;
    } catch (error) {
      if (!error.response) throw new Error('Network error - Please check your connection');
      const reason = error.response.data?.error;
      switch (error.response.status) {
        case 403: throw new Error('You can only transfer your own call');
        case 404: throw new Error('That call or rep was not found');
        case 422: throw new Error(typeof reason === 'string' ? reason : 'That rep cannot take transfers');
        case 502: throw new Error('The phone system did not complete the transfer');
        default: throw new Error('The transfer failed. Try again.');
      }
    }
  },

  holdCall: async (callId) => {
    try {
      if (typeof callId !== 'string' || !callId) throw new Error('callId is required');
      const response = await dialerApi.put(`/calls/${callId}/hold`);
      return response.data;
    } catch (error) {
      if (!error.response) throw new Error('Network error - Please check your connection');
      switch (error.response.status) {
        case 401: throw new Error('Authentication required');
        case 404: throw new Error('Call not found');
        case 500: throw new Error('Server error - Please try again later');
        default: throw new Error(`API Error: ${error.response.statusText}`);
      }
    }
  },

  /**
   * Resume a held call.
   * @param {string} callId - ID of the held call
   */
  unholdCall: async (callId) => {
    try {
      if (typeof callId !== 'string' || !callId) throw new Error('callId is required');
      const response = await dialerApi.put(`/calls/${callId}/unhold`);
      return response.data;
    } catch (error) {
      if (!error.response) throw new Error('Network error - Please check your connection');
      switch (error.response.status) {
        case 401: throw new Error('Authentication required');
        case 404: throw new Error('Call not found');
        case 500: throw new Error('Server error - Please try again later');
        default: throw new Error(`API Error: ${error.response.statusText}`);
      }
    }
  },

  /**
   * Pause or un-pause an agent within an Asterisk queue.
   *
   * IMPORTANT: This is agent-level queue membership status (i.e. "this agent
   * temporarily stops receiving queue calls"), NOT call-level mute. Do not
   * use this as a mute implementation — mute is handled via the SIP/WebRTC
   * layer through toggleMute() in IntegratedDialerContext.
   */
  setQueuePause: async (params) => {
    try {
      validateTypes(params, {
        queueName: "string",
        paused: "boolean",
      });

      const response = await dialerApi.post(
        "/api/asterisk/queue/pause",
        params,
      );
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw new Error("Network error - Please check your connection");
      }

      switch (error.response.status) {
        case 400:
          throw new Error("Invalid request parameters");
        case 401:
          throw new Error("Authentication required");
        case 500:
          throw new Error("Server error - Please try again later");
        default:
          throw new Error(`API Error: ${error.response.statusText}`);
      }
    }
  },
};
